/**
 * translationFidelity — proving a translation says what the source says.
 *
 * A translator that is allowed to improve the text will quietly change a rate,
 * drop a decree number or invent a citation, and every other check we run
 * (language, length, echo) passes it. So the facts that must survive any
 * translation are compared directly against the source: figures, links and
 * acronyms.
 *
 * Deliberately forgiving where a faithful translation legitimately differs:
 * single digits are often spelled out in words, two-letter acronyms (EU, US)
 * have local forms, and thousand separators differ by language.
 *
 * Mirrors supabase/functions/_shared/localization/translation-fidelity.ts —
 * the two must stay in step.
 */

export interface FidelityGap {
  /** Facts in the source that the translation does not carry. */
  missing: string[];
  /** Facts the translation states that the source never did. */
  added: string[];
  /**
   * Differences a faithful translation legitimately produces: an acronym written
   * in the local form, or a figure the source spelled out in words. Reported so a
   * reviewer can see them, never enough on their own to refuse the text.
   */
  soft?: string[];
}

/**
 * Every digit block a language we publish in writes its numbers with.
 *
 * This listed Arabic, Persian, Devanagari and Thai. Bengali, Gurmukhi and Telugu
 * are enabled primary locales and were not here, so a figure one of them wrote
 * in its own digits \u2014 \u09E7\u09EF\u09EF\u09E8, \u0A67\u0A6F\u0A6F\u0A68, \u0C67\u0C6F\u0C6F\u0C68 \u2014 never normalised to 1992. It was read
 * as a figure the source did not state *and* the source's 1992 was read as
 * dropped, so a correct translation was refused twice over for writing its own
 * numerals.
 *
 * The rest of the Indic blocks are here for the same reason, before a locale is
 * enabled rather than after it has produced a corpus nobody can grade.
 */
const NATIVE_DIGITS =
  /[\u0660-\u0669\u06F0-\u06F9\u0966-\u096F\u09E6-\u09EF\u0A66-\u0A6F\u0AE6-\u0AEF\u0B66-\u0B6F\u0BE6-\u0BEF\u0C66-\u0C6F\u0CE6-\u0CEF\u0D66-\u0D6F\u0E50-\u0E59]/g;

const DIGIT_BASES: Array<[number, number]> = [
  [0x0660, 0x0669], // Arabic-Indic
  [0x06f0, 0x06f9], // Extended Arabic-Indic (Persian, Urdu)
  [0x0966, 0x096f], // Devanagari (Hindi, Marathi)
  [0x09e6, 0x09ef], // Bengali
  [0x0a66, 0x0a6f], // Gurmukhi (Punjabi)
  [0x0ae6, 0x0aef], // Gujarati
  [0x0b66, 0x0b6f], // Oriya
  [0x0be6, 0x0bef], // Tamil
  [0x0c66, 0x0c6f], // Telugu
  [0x0ce6, 0x0cef], // Kannada
  [0x0d66, 0x0d6f], // Malayalam
  [0x0e50, 0x0e59], // Thai
];

/** Native digits rewritten as 0-9 so the same figure compares equal. */
function toAsciiDigits(value: string): string {
  return value.replace(NATIVE_DIGITS, (character) => {
    const code = character.codePointAt(0) ?? 0;
    for (const [start, end] of DIGIT_BASES) {
      if (code >= start && code <= end) return String(code - start);
    }
    return character;
  });
}

function plainText(value: string): string {
  return toAsciiDigits(
    value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&'),
  );
}

const URL_PATTERN = /https?:\/\/[^\s<>"')]+/gi;

/**
 * One figure, with the separators that belong inside a figure and no others.
 *
 * This was `/\d[\d.,\u00A0\s]*\d|\d/`, which spans an ordinary space \u2014 so it did
 * not read one figure, it read a run of them. English "of 21 November 2005"
 * gives `21` and `2005`; the same sentence in Portuguese, "de 21 de novembro de
 * 2005", also gives `21` and `2005`; but English "Law 11.196, of 21 December
 * 2005" puts a comma and a space between two figures and the old pattern
 * swallowed both into `212005`. The two texts state the same date and shared not
 * one token.
 *
 * The residue was unmistakable in the corpus: 202023, 261995, 222018, 312022,
 * 51946, 211966, 199611 \u2014 glued dates, every one, reported as figures the
 * translation invented and figures the source lost. Measured 2026-09-21 over all
 * 7,525 topic language versions, not gluing takes the rows this comparison
 * refuses from 353 to 214.
 *
 * A thousands separator is still part of the figure, so the narrow spaces a
 * language actually uses for one \u2014 no-break and thin \u2014 stay in the class. An
 * ordinary space does not: nothing writes 1 000 000 with plain spaces in this
 * corpus, and admitting it is what cost the dates.
 */
const NUMBER_PATTERN = /\d+(?:[.,\u00A0\u202F\u2009]\d+)*/g;
const ACRONYM_PATTERN = /\b[A-Z][A-Z0-9]{2,}\b/g;

/** A figure as a comparable string: separators and trailing noise removed. */
function normalizeNumber(raw: string): string {
  return raw.replace(/[.,\u00A0\u202F\u2009]/g, '');
}

function numbersIn(text: string): string[] {
  const found: string[] = [];
  for (const match of text.matchAll(NUMBER_PATTERN)) {
    const normalized = normalizeNumber(match[0]);
    // A single digit is routinely written as a word in the other language.
    if (normalized.length < 2) continue;
    found.push(normalized);
  }
  return found;
}

function acronymsIn(text: string): string[] {
  return Array.from(text.matchAll(ACRONYM_PATTERN)).map((match) => match[0]);
}

function urlsIn(text: string): string[] {
  return Array.from(text.matchAll(URL_PATTERN)).map((match) => match[0].replace(/[.,;]$/, ''));
}

/**
 * Values present in `wanted` but not in `have`, each reported once.
 *
 * Membership, not occurrence count. This compared multisets: a source stating
 * 2023 twice and a translation stating it once left one unmatched, and the
 * translation was reported for dropping a figure it plainly carries. The same
 * arithmetic in the other direction reported a translation that repeats a figure
 * as having invented one.
 *
 * Measured over the topic corpus on 2026-09-20: of 738 rows the body comparison
 * refused, 206 genuinely lost a figure and 149 genuinely introduced one, while
 * 73 differed only in how many times a figure appeared and 311 merely repeated
 * one the source already had. More than half of what this rule refused was the
 * counting, not the facts.
 *
 * How often a translation repeats a fact is a matter of style; whether it states
 * the fact is the thing worth refusing over.
 */
function absent(wanted: string[], have: string[]): string[] {
  const pool = new Set(have);
  const missing: string[] = [];
  for (const value of wanted) {
    if (pool.has(value)) continue;
    if (!missing.includes(value)) missing.push(value);
  }
  return missing;
}

/**
 * The facts a translation of this text will be graded on.
 *
 * Read by the prompt contract so the writer is handed the same figures, links and
 * acronyms that `fidelityGaps` will look for afterwards. Extracted here, beside
 * the comparison, so the two can never drift into asking for one set and checking
 * another.
 */
export function factsIn(text: string): { numbers: string[]; urls: string[]; acronyms: string[] } {
  const plain = plainText(text ?? '');
  const unique = (values: string[]): string[] => Array.from(new Set(values));
  return {
    numbers: unique(numbersIn(plain)),
    urls: unique(urlsIn(plain)),
    acronyms: unique(acronymsIn(plain)),
  };
}

/**
 * What the translation changed about the facts. An empty result means every
 * figure, link and acronym in the source survived and nothing was invented.
 */
export function fidelityGaps(source: string, candidate: string): FidelityGap {
  const from = plainText(source ?? '');
  const to = plainText(candidate ?? '');

  const sourceNumbers = numbersIn(from);
  const candidateNumbers = numbersIn(to);
  const sourceUrls = urlsIn(from);
  const candidateUrls = urlsIn(to);
  const sourceAcronyms = acronymsIn(from);
  const candidateAcronyms = acronymsIn(to);

  const missing = [
    ...absent(sourceNumbers, candidateNumbers),
    ...absent(sourceUrls, candidateUrls),
  ];
  // Only figures and links count as invention: an acronym can appear in the
  // target language as the local form of a term the source spelled out.
  const addedNumbers = absent(candidateNumbers, sourceNumbers);
  const added = [
    // A figure the source wrote as a word ("thirty days") and the translation
    // wrote as a numeral is the same fact, not an invented one.
    ...addedNumbers.filter(value => !spelledOutInSource(value, from)),
    ...absent(candidateUrls, sourceUrls),
  ];
  // Correct translations routinely localise an acronym (VAT → IVA, Mwst) or
  // spell the term out, so an acronym the translation does not repeat is
  // reported but never refuses the text on its own.
  const soft = [
    ...absent(sourceAcronyms, candidateAcronyms),
    ...addedNumbers.filter(value => spelledOutInSource(value, from)),
  ];

  return { missing, added, soft };
}

/**
 * Figures a source language commonly writes as words. A translation that writes
 * the numeral instead states the same fact, so it is not an invention.
 *
 * Months are here for the same reason, and they are the larger half of it. A
 * date is a figure in Japanese, Korean, Chinese and Vietnamese the way it is a
 * word in English: "31 December 2025" is 2025年12月31日, and ngày 31 tháng 12.
 * The month is not an extra figure the translation made up — it is the only way
 * those languages can write the month the source named.
 *
 * Measured 2026-09-21: of the 214 rows this comparison still refused after the
 * number pattern stopped gluing dates, 160 were in ja, ko, zh, zh-TW and vi, and
 * the added figures were 12, 11 and 10 — December, November, October. Both
 * source languages are covered because a topic's source is only ever English or
 * Portuguese; a third source language needs its months added here.
 *
 * Only 10, 11 and 12 need listing: `numbersIn` already passes over a single
 * digit, so January through September never reach this comparison.
 */
const NUMBER_WORDS: Record<string, readonly string[]> = {
  '10': ['ten', 'dez', 'diez', 'dix', 'zehn', 'dieci', 'tien', 'october', 'outubro'],
  '11': ['eleven', 'onze', 'once', 'elf', 'undici', 'november', 'novembro'],
  '12': ['twelve', 'doze', 'doce', 'douze', 'zwölf', 'dodici', 'twaalf', 'december', 'dezembro'],
  '15': ['fifteen', 'quinze', 'quince', 'fünfzehn', 'quindici'],
  '20': ['twenty', 'vinte', 'veinte', 'vingt', 'zwanzig', 'venti', 'twintig'],
  '24': ['twenty-four', 'twenty four'],
  '30': ['thirty', 'trinta', 'treinta', 'trente', 'dreißig', 'trenta', 'dertig'],
  '50': ['fifty', 'cinquenta', 'cincuenta', 'cinquante', 'fünfzig', 'cinquanta'],
  '100': ['hundred', 'cem', 'cien', 'cent', 'hundert', 'cento', 'honderd'],
  '1000': ['thousand', 'mil', 'mille', 'tausend', 'mila', 'duizend'],
};

/**
 * The source says this figure in words rather than digits — as a number word, or
 * as the name of a month the target language can only write as a numeral.
 *
 * Exported so `findUnsourcedFigures` can ask the same question. A block carrying
 * a rate and a date fails both rules or neither, and it should not be this rule
 * set that decides a Japanese date is an unsourced figure while the fidelity
 * comparison beside it decides the same date is fine.
 */
export function spelledOutInSource(value: string, source: string): boolean {
  const words = NUMBER_WORDS[value];
  if (!words) return false;
  const lower = source.toLowerCase();
  return words.some(word => lower.includes(word));
}

function list(values: string[]): string {
  return values.slice(0, 4).join(', ');
}

/**
 * A plain sentence naming what the translation changed, or an empty string when
 * it is faithful.
 */
export function fidelityMessage(gap: FidelityGap): string {
  const parts: string[] = [];
  if (gap.missing.length > 0) {
    parts.push(`the source states ${list(gap.missing)} and the translation does not`);
  }
  if (gap.added.length > 0) {
    parts.push(`the translation states ${list(gap.added)} and the source does not`);
  }
  if (parts.length === 0) return '';
  return `${parts.join('; ')} — translate exactly what the source says, keeping every figure and reference unchanged.`;
}

/** True when nothing was added or removed. */
export function isFaithful(gap: FidelityGap): boolean {
  return gap.missing.length === 0 && gap.added.length === 0;
}
